import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';

interface UploadedVideoItem {
  url?: string;
  thumbnailUrl?: string;
  name?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-user-videos-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MaterialModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>video_library</mat-icon>
      Uploaded Videos -
      {{ data.user?.fullName || data.user?.username || 'Athlete' }}
      <span class="video-count">({{ videos.length }} videos)</span>
    </h2>
    <mat-dialog-content>
      <div *ngIf="!videos || videos.length === 0" class="empty">
        <mat-icon>video_library_off</mat-icon>
        <p>No videos uploaded by this user.</p>
      </div>

      <div *ngIf="videos && videos.length > 0" class="content-wrapper">
        <!-- Action buttons above videos -->
        <div class="video-actions-bar">
          <button
            mat-raised-button
            color="primary"
            (click)="downloadAllVideos()"
            class="action-button"
          >
            <mat-icon>download</mat-icon>
            Download All
          </button>
          <button
            mat-icon-button
            mat-dialog-close
            class="close-button"
            matTooltip="Close"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- Video grid -->
        <div class="videos-grid">
          <div class="video-card" *ngFor="let v of pagedVideos; let i = index">
            <div class="video-header">
              <div class="video-info">
                <h4 class="title">{{ getTitle(v) }}</h4>
                <div class="video-stats">
                  <span class="stat">
                    <mat-icon>timer</mat-icon>
                    {{ getDuration(v) }}s
                  </span>
                  <span class="stat">
                    <mat-icon>fitness_center</mat-icon>
                    {{ getPunchCount(v) }} punches
                  </span>
                  <span class="stat">
                    <mat-icon>schedule</mat-icon>
                    {{ getCreatedDate(v) }}
                  </span>
                </div>
              </div>
              <div class="video-actions">
                <button
                  mat-icon-button
                  (click)="downloadVideo(v)"
                  matTooltip="Download Video"
                >
                  <mat-icon>download</mat-icon>
                </button>
                <button
                  mat-icon-button
                  (click)="openVideoInNewTab(v)"
                  matTooltip="Open in New Tab"
                >
                  <mat-icon>open_in_new</mat-icon>
                </button>
              </div>
            </div>

            <div class="video-container">
              <!-- Thumbnail - only show when NOT playing -->
              <div class="thumb" *ngIf="!isVideoPlaying(i) && getThumb(v)">
                <img [src]="getThumb(v)" alt="thumbnail" />
              </div>

              <!-- No thumbnail placeholder - only show when NOT playing -->
              <div
                class="thumb placeholder"
                *ngIf="!isVideoPlaying(i) && !getThumb(v)"
                (click)="toggleVideo(v, i)"
              >
                <mat-icon>video_file</mat-icon>
                <p>No thumbnail available</p>
                <div class="play-button-overlay">
                  <mat-icon>play_arrow</mat-icon>
                </div>
              </div>

              <!-- Video player - show when playing -->
              <video
                *ngIf="getUrl(v) && isVideoPlaying(i)"
                class="player"
                [src]="getUrl(v)"
                controls
                autoplay
                muted
                (loadedmetadata)="onVideoLoaded($event, v)"
                (canplay)="onVideoCanPlay($event, v)"
                (error)="onVideoError($event, v)"
              ></video>

              <!-- Play overlay - only show when NOT playing -->
              <div
                class="play-overlay"
                *ngIf="!isVideoPlaying(i) && getUrl(v)"
                (click)="toggleVideo(v, i)"
              >
                <mat-icon>play_arrow</mat-icon>
              </div>

              <!-- Pause overlay - show when video is playing -->
              <div
                class="pause-overlay"
                *ngIf="isVideoPlaying(i)"
                (click)="toggleVideo(v, i)"
              >
                <mat-icon>pause</mat-icon>
              </div>
            </div>

            <div class="video-footer">
              <span class="status" [class]="getStatusClass(v)">
                {{ getStatus(v) }}
              </span>
            </div>
          </div>
        </div>

        <div class="paginator" *ngIf="videos && videos.length > pageSize">
          <button
            mat-stroked-button
            color="primary"
            (click)="prevPage()"
            [disabled]="pageIndex === 0"
          >
            <mat-icon>chevron_left</mat-icon>
            Previous
          </button>
          <span class="page-info"
            >Page {{ pageIndex + 1 }} of {{ totalPages }}</span
          >
          <button
            mat-stroked-button
            color="primary"
            (click)="nextPage()"
            [disabled]="pageIndex + 1 >= totalPages"
          >
            Next
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>
      </div>
    </mat-dialog-content>
  `,
  styles: [
    `
      .videos-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 20px;
      }
      .video-card {
        background: #1a1a1a;
        border-radius: 12px;
        padding: 16px;
        color: #fff;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      }

      .video-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
      }
      .video-info {
        flex: 1;
      }
      .title {
        font-weight: 600;
        font-size: 16px;
        margin: 0 0 8px 0;
        color: #fff;
      }
      .video-stats {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }
      .stat {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #aaa;
      }
      .stat mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }

      .video-actions {
        display: flex;
        gap: 4px;
      }
      .video-actions button {
        color: #90caf9;
      }

      .video-container {
        position: relative;
        margin-bottom: 12px;
        height: 180px;
        border-radius: 8px;
        overflow: hidden;
      }
      .thumb {
        height: 180px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #222;
        border-radius: 8px;
        overflow: hidden;
        position: relative;
      }
      .thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .thumb.placeholder {
        color: #aaa;
        font-size: 14px;
        flex-direction: column;
        gap: 8px;
        cursor: pointer;
        position: relative;
      }
      .thumb.placeholder mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
      }
      .play-button-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.7);
        border-radius: 50%;
        width: 60px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s;
      }
      .thumb.placeholder:hover .play-button-overlay {
        opacity: 1;
      }
      .play-button-overlay mat-icon {
        font-size: 32px;
        width: 32px;
        height: 32px;
        color: #fff;
      }

      .play-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.3s;
        border-radius: 8px;
        z-index: 10;
      }
      .play-overlay:hover {
        background: rgba(0, 0, 0, 0.7);
      }
      .play-overlay mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: #fff;
      }

      .pause-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.3s;
        border-radius: 8px;
        z-index: 10;
        opacity: 0;
      }
      .pause-overlay:hover {
        background: rgba(0, 0, 0, 0.6);
        opacity: 1;
      }
      .pause-overlay mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: #fff;
      }

      .player {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }

      .video-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .status {
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
      }
      .status.active {
        background: #4caf50;
        color: #fff;
      }
      .status.inactive {
        background: #f44336;
        color: #fff;
      }
      .status.pending {
        background: #ff9800;
        color: #fff;
      }

      .paginator {
        display: flex;
        align-items: center;
        gap: 16px;
        justify-content: center;
        margin-top: 20px;
      }
      .page-info {
        color: #aaa;
        font-size: 14px;
      }

      .empty {
        color: #aaa;
        text-align: center;
        padding: 40px;
      }
      .empty mat-icon {
        font-size: 64px;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
      }

      mat-dialog-title {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 16px 24px 0 24px;
      }

      mat-dialog-title mat-icon {
        color: #90caf9;
      }
      .mat-mdc-dialog-title,
      h2.mat-mdc-dialog-title,
      mat-dialog-title {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 16px 24px 0 24px;
        color: #ffffff !important;
      }

      .video-count {
        color: #aaa;
        font-size: 14px;
        font-weight: normal;
      }

      .content-wrapper {
        padding: 16px 24px;
      }

      .video-actions-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding: 12px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }

      .action-button {
        background: linear-gradient(
          135deg,
          #ef4444 0%,
          #dc2626 100%
        ) !important;
        color: #fff !important;
        border-radius: 8px !important;
        font-weight: 600 !important;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35) !important;
      }

      .action-button:hover {
        filter: brightness(1.1) !important;
      }

      .action-button mat-icon {
        color: #fff !important;
        margin-right: 8px;
      }

      .close-button {
        color: #94a3b8 !important;
        background-color: transparent;
      }

      .close-button:hover {
        color: #ffffff !important;
        background-color: rgba(255, 255, 255, 0.1) !important;
      }

      .close-button mat-icon {
        color: inherit !important;
      }
    `,
  ],
})
export class UserVideosDialogComponent {
  videos: UploadedVideoItem[] = [];
  playingVideos: Set<number> = new Set();

  pageIndex = 0;
  pageSize = 6;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { user: any; videos: UploadedVideoItem[] },
    private dialogRef: MatDialogRef<UserVideosDialogComponent>,
  ) {
    this.videos = Array.isArray(data?.videos) ? data.videos : [];
    console.log('Videos data received:', this.videos);
    console.log('User data:', this.data.user);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.videos.length / this.pageSize));
  }

  get pagedVideos(): UploadedVideoItem[] {
    const start = this.pageIndex * this.pageSize;
    return this.videos.slice(start, start + this.pageSize);
  }

  nextPage() {
    if (this.pageIndex + 1 < this.totalPages) this.pageIndex++;
  }
  prevPage() {
    if (this.pageIndex > 0) this.pageIndex--;
  }

  getUrl(v: UploadedVideoItem): string | null {
    if (!v) return null;
    if (typeof v === 'string') return v as unknown as string;
    return v.url || v['videoUrl'] || null;
  }

  getThumb(v: UploadedVideoItem): string | null {
    if (!v) return null;
    if (typeof v === 'string') return null;
    return v.thumbnailUrl || v['thumbUrl'] || v['thumbnail'] || null;
  }

  getName(v: UploadedVideoItem): string {
    if (!v) return 'Video';
    if (typeof v === 'string') {
      try {
        return new URL(v).pathname.split('/').pop() || 'Video';
      } catch {
        return 'Video';
      }
    }
    return v.name || v['fileName'] || 'Video';
  }

  getTitle(v: UploadedVideoItem): string {
    if (!v) return 'Untitled Video';
    if (typeof v === 'string') return 'Video';
    return v['title'] || v.name || 'Untitled Video';
  }

  getDuration(v: UploadedVideoItem): string {
    if (!v) return '0';
    if (typeof v === 'string') return '0';
    return v['duration'] || '0';
  }

  getPunchCount(v: UploadedVideoItem): string {
    if (!v) return '0';
    if (typeof v === 'string') return '0';
    return v['punchCount'] || '0';
  }

  getStatus(v: UploadedVideoItem): string {
    if (!v) return 'Unknown';
    if (typeof v === 'string') return 'Unknown';
    return v['status'] || 'Unknown';
  }

  getStatusClass(v: UploadedVideoItem): string {
    const status = this.getStatus(v).toLowerCase();
    return status === 'active'
      ? 'active'
      : status === 'inactive'
        ? 'inactive'
        : 'pending';
  }

  getCreatedDate(v: UploadedVideoItem): string {
    if (!v) return 'Unknown';
    if (typeof v === 'string') return 'Unknown';
    if (v['createdAt']) {
      return new Date(v['createdAt']).toLocaleDateString();
    }
    return 'Unknown';
  }

  isVideoPlaying(index: number): boolean {
    return this.playingVideos.has(index);
  }

  toggleVideo(v: UploadedVideoItem, index: number): void {
    console.log(
      'Toggle video clicked:',
      index,
      'Current playing:',
      this.playingVideos.has(index),
    );
    console.log('Video URL:', this.getUrl(v));

    if (this.playingVideos.has(index)) {
      this.playingVideos.delete(index);
      console.log('Video paused');
    } else {
      this.playingVideos.add(index);
      console.log('Video started playing');
    }
  }

  onVideoLoaded(event: any, video: UploadedVideoItem): void {
    // Video loaded successfully
    console.log('Video loaded:', video);
    const videoElement = event.target;
    if (videoElement) {
      videoElement.play().catch((error: any) => {
        console.log('Autoplay failed:', error);
        // If autoplay fails, we'll let the user click play manually
      });
    }
  }

  onVideoCanPlay(event: any, video: UploadedVideoItem): void {
    console.log('Video can play:', video);
    const videoElement = event.target;
    if (videoElement) {
      videoElement.play().catch((error: any) => {
        console.log('Play failed:', error);
      });
    }
  }

  onVideoError(event: any, video: UploadedVideoItem): void {
    console.error('Video error:', event, video);
  }

  downloadVideo(video: UploadedVideoItem): void {
    const url = this.getUrl(video);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = this.getTitle(video) + '.mp4';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  openVideoInNewTab(video: UploadedVideoItem): void {
    const url = this.getUrl(video);
    if (url) {
      window.open(url, '_blank');
    }
  }

  downloadAllVideos(): void {
    this.videos.forEach((video, index) => {
      setTimeout(() => {
        this.downloadVideo(video);
      }, index * 1000); // Delay each download by 1 second
    });
  }
}
